import { useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../../../services/subscription';
import { toast } from 'react-toastify';
import { useTranslation } from '@hooks/useTranslation';

export interface ReactivateSubscriptionInput {
  organizationId: string;
  billingCycle: string;
  planId?: string;
  // Option to use existing payment method
  useExistingPaymentMethod?: boolean;
  existingPaymentMethodId?: string;
  // Required only if NOT using existing payment method
  paymentMethodType?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  ccNumber?: string;
  ccExp?: string;
  cvv?: string;
}

interface UseReactivateSubscriptionOptions {
  /**
   * If true, skip the automatic redirect after success.
   * Useful when reactivating from profile page where we just want to refresh the data.
   */
  skipRedirect?: boolean;
}

export const useReactivateSubscription = (options: UseReactivateSubscriptionOptions = {}) => {
  const tActionMessages = useTranslation('actionMessages');
  const { skipRedirect = true } = options; // Default to NOT redirecting
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ReactivateSubscriptionInput) =>
      subscriptionService.reactivateSubscription(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationSubscription'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['userOrganizations'] });

      toast.success(tActionMessages.subscriptionReactivatedSuccessfully);

      // Only redirect if explicitly requested (skipRedirect = false)
      if (!skipRedirect) {
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.errors?.[0]?.message ||
        error?.message ||
        'Failed to reactivate subscription';
      toast.error(errorMessage);
    },
  });
};
